import { z } from "zod";

/**
 * LES AGENTS DE FLUX : une etape `agent` confie une consigne a un modele
 * (Claude), avec des OUTILS choisis dans une liste (les actions MyTaki du
 * catalogue de Flux, et les outils que le MCP de l'org expose), une CLE API
 * de l'org rangee cote serveur, un PLAFOND de depense par execution, et un
 * JOURNAL (consigne, appels d'outils, reponse, cout) dans la trace.
 *
 * Ce fichier est le CONTRAT commun : l'app Flux (builder, page Cles,
 * historique), le moteur (`functions/src/flux/agent.ts`, recu par le bundle
 * `functions/build-studio.mjs`), le MCP. Le raisonnement : `docs/socle/flux-agents.md`.
 */

// ---------------------------------------------------------------------------
// Modeles
// ---------------------------------------------------------------------------

export interface FluxModeleAgent {
  /** Identifiant du fournisseur, tel qu'envoye a l'API. */
  id: string;
  libelle: string;
  fournisseur: FluxFournisseurCle;
  /** Dollars par million de jetons : entree, sortie, lecture de cache, ecriture de cache. */
  prix: { entree: number; sortie: number; cacheLu: number; cacheEcrit: number };
}

/** Les fournisseurs dont une cle se range dans la page « Clés ». */
export const FLUX_FOURNISSEURS_CLE = ["anthropic"] as const;
export type FluxFournisseurCle = (typeof FLUX_FOURNISSEURS_CLE)[number];
export const LIBELLES_FOURNISSEUR_CLE: Readonly<Record<FluxFournisseurCle, string>> = { anthropic: "Anthropic (Claude)" };

/**
 * Les modeles qu'une etape agent peut viser. Prix publics d'Anthropic
 * (API directe), l'ecriture de cache a 1,25 fois l'entree. Un modele retire
 * d'ici rend la definition invalide a la publication suivante.
 */
export const FLUX_MODELES_AGENT: readonly FluxModeleAgent[] = [
  { id: "claude-sonnet-5-5", libelle: "Claude Sonnet 5.5", fournisseur: "anthropic", prix: { entree: 2, sortie: 10, cacheLu: 0.2, cacheEcrit: 2.5 } },
  { id: "claude-opus-5-5", libelle: "Claude Opus 5.5", fournisseur: "anthropic", prix: { entree: 4, sortie: 20, cacheLu: 0.2, cacheEcrit: 5 } },
  { id: "claude-haiku-5-5", libelle: "Claude Haiku 5.5", fournisseur: "anthropic", prix: { entree: 0.1, sortie: 0.5, cacheLu: 0.01, cacheEcrit: 0.125 } },
];
export const FLUX_MODELE_AGENT_DEFAUT = "claude-sonnet-5-5";

export function modeleAgent(id: string): FluxModeleAgent | undefined {
  return FLUX_MODELES_AGENT.find((m) => m.id === id);
}

/** L'usage rendu par le fournisseur, en jetons. */
export interface FluxUsageAgent {
  entree: number;
  sortie: number;
  cacheLu: number;
  cacheEcrit: number;
}

/** Le cout en dollars d'un usage, pour un modele. */
export function coutAgent(modele: FluxModeleAgent, usage: FluxUsageAgent): number {
  const { prix } = modele;
  return (usage.entree * prix.entree + usage.sortie * prix.sortie + usage.cacheLu * prix.cacheLu + usage.cacheEcrit * prix.cacheEcrit) / 1_000_000;
}

// ---------------------------------------------------------------------------
// Plafonds
// ---------------------------------------------------------------------------

/** Plafond de depense d'UNE execution de l'etape, en dollars. */
export const FLUX_AGENT_PLAFOND_DEFAUT = 0.25;
export const FLUX_AGENT_PLAFOND_MAX = 5;
export const FLUX_AGENT_PLAFOND_MIN = 0.01;
/** Tours de modele (un appel a l'API = un tour) d'une execution. */
export const FLUX_AGENT_TOURS_DEFAUT = 8;
export const FLUX_AGENT_TOURS_MAX = 20;
/** Outils nommes par une etape, au plus. */
export const FLUX_AGENT_OUTILS_MAX = 60;
/** Champs d'une sortie structuree, au plus. */
export const FLUX_AGENT_CHAMPS_MAX = 12;
export const FLUX_AGENT_CONSIGNE_MAX = 8000;
/** Plafond QUOTIDIEN d'une cle (toutes executions de l'org confondues), defaut et bornes, en dollars. */
export const FLUX_CLE_PLAFOND_JOUR_DEFAUT = 10;
export const FLUX_CLE_PLAFOND_JOUR_MAX = 500;

/**
 * Le nom de l'outil interne par lequel l'agent rend sa sortie structuree
 * (`champsSortie`). Jamais une action du catalogue (elles sont en anglais).
 */
export const FLUX_AGENT_OUTIL_SORTIE = "rendre_resultat";

// ---------------------------------------------------------------------------
// L'etape
// ---------------------------------------------------------------------------

const CLE_CHAMP = /^[a-z][a-zA-Z0-9_]{0,39}$/;
const NOM_ACTION = /^[a-z][a-z0-9_]{0,79}$/;

export const FluxChampSortieAgentSchema = z
  .object({
    /** La cle lue par l'etape suivante : `{{step_N.donnees.<cle>}}`. */
    cle: z.string().regex(CLE_CHAMP, "Clé invalide (lettres, chiffres, _ ; commence par une minuscule)."),
    description: z.string().trim().min(1, "Description requise.").max(300),
  })
  .strict();
export type FluxChampSortieAgent = z.infer<typeof FluxChampSortieAgentSchema>;

/**
 * Les champs PROPRES a l'etape agent (les champs communs, `id`, `nom`,
 * `desactivee`, `continuerSiErreur`, s'ajoutent dans `flux.ts`).
 */
export const FluxEtapeAgentChampsSchema = {
  /**
   * La consigne ; references `{{trigger.fiche.name}}`, `{{step_1.x}}` admises.
   * Vide admise dans un BROUILLON (l'etape vient d'etre posee) : la publication
   * et l'execution la refusent (`problemesDesAgents`, `functions/src/flux/services.ts`).
   */
  consigne: z.string().trim().max(FLUX_AGENT_CONSIGNE_MAX).default(""),
  modele: z
    .string()
    .default(FLUX_MODELE_AGENT_DEFAUT)
    .refine((id) => modeleAgent(id) !== undefined, "Modèle inconnu."),
  /** L'id d'une cle de l'org (`orgs/{orgId}/fluxCles/{id}`). Absente : l'etape echoue a l'execution, et la publication le dit. */
  cle: z.string().trim().min(1).max(64).optional(),
  /** Les actions du catalogue que l'agent peut appeler (droits de l'auteur, comme une etape Taki). */
  outils: z.array(z.string().regex(NOM_ACTION, "Outil inconnu.")).max(FLUX_AGENT_OUTILS_MAX).default([]),
  /** Ajoute les outils que le MCP de l'org expose (mode restreint ou complet), hors administration et suppression. */
  outilsMcp: z.boolean().optional(),
  /** Une sortie STRUCTUREE : l'agent rend ces champs (texte), lus par `{{step_N.donnees.<cle>}}`. */
  champsSortie: z.array(FluxChampSortieAgentSchema).max(FLUX_AGENT_CHAMPS_MAX).optional(),
  plafondDollars: z.number().min(FLUX_AGENT_PLAFOND_MIN).max(FLUX_AGENT_PLAFOND_MAX).default(FLUX_AGENT_PLAFOND_DEFAUT),
  toursMax: z.number().int().min(1).max(FLUX_AGENT_TOURS_MAX).optional(),
};

export interface FluxEtapeAgentChamps {
  consigne: string;
  modele: string;
  cle?: string;
  outils: string[];
  outilsMcp?: boolean;
  champsSortie?: FluxChampSortieAgent[];
  plafondDollars: number;
  toursMax?: number;
}

/**
 * Combien de fois une automatisation peut lancer une etape agent par heure (UTC),
 * toutes fiches confondues : un agent qui ecrit une fiche peut en declencher un
 * autre, et la cadence borne une boucle entre automatisations, en nombre. Les
 * executions de TEST n'y comptent pas.
 */
export const FLUX_AGENT_PASSAGES_HEURE_MAX = 30;

/**
 * Pourquoi l'agent s'est arrete. `fin` : il a repondu ; `erreur` : le fournisseur a echoue (cle refusee, 429, reseau)
 * ou l'agent ne peut plus continuer (auteur desactive, modele servi inattendu) ; `arrete` : quelqu'un a arrete
 * l'execution pendant qu'il tournait ; les autres : un plafond l'a coupe.
 */
export const FLUX_ARRETS_AGENT = ["fin", "plafond", "tours", "duree", "refus", "longueur", "erreur", "arrete"] as const;
export type FluxArretAgent = (typeof FLUX_ARRETS_AGENT)[number];

/** Un appel d'outil du journal. */
export interface FluxAppelOutilAgent {
  outil: string;
  entree: unknown;
  sortie?: unknown;
  erreur?: string;
  dureeMs: number;
}

/**
 * LA SORTIE de l'etape agent, deposee sous son id dans le contexte (et dans la
 * trace, ou elle sert de JOURNAL). L'etape suivante lit `{{step_N.texte}}`, ou
 * `{{step_N.donnees.<cle>}}` si l'etape declare `champsSortie`.
 */
export interface FluxSortieAgent {
  texte: string;
  donnees: Record<string, unknown>;
  modele: string;
  appels: FluxAppelOutilAgent[];
  tours: number;
  arret: FluxArretAgent;
  cout: FluxUsageAgent & { dollars: number };
}

// ---------------------------------------------------------------------------
// Les cles API de l'org
// ---------------------------------------------------------------------------

/**
 * Le document `orgs/{orgId}/fluxCles/{id}` : la FICHE d'une cle, jamais sa
 * valeur (Secret Manager en ligne, un fichier local chiffre sous l'emulateur).
 * Lu par l'owner et les admins de l'org, ecrit par le serveur seul.
 */
export interface FluxCle {
  id: string;
  orgId: string;
  nom: string;
  fournisseur: FluxFournisseurCle;
  /** « ••••abcd » : les quatre derniers caracteres, pour reconnaitre la cle. */
  apercu: string;
  plafondJourDollars: number;
  /** Depense du jour UTC (`jour` AAAA-MM-JJ), ajoutee apres chaque execution d'agent. */
  depenseJour: { jour: string; dollars: number } | null;
  derniereUtilisation: Date | null;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

const nomCle = z.string().trim().min(1, "Nom requis.").max(60, "Nom trop long (60 caractères).");
const valeurCle = z.string().trim().min(20, "Clé trop courte.").max(400, "Clé trop longue.").regex(/^\S+$/, "Une clé ne contient pas d'espace.");
const plafondJour = z.number().min(0.1).max(FLUX_CLE_PLAFOND_JOUR_MAX);

/** `cle_creer` : la valeur part au serveur, qui ne la rend jamais. */
export const FluxCleSaisieSchema = z
  .object({
    nom: nomCle,
    fournisseur: z.enum(FLUX_FOURNISSEURS_CLE).default("anthropic"),
    valeur: valeurCle,
    plafondJourDollars: plafondJour.default(FLUX_CLE_PLAFOND_JOUR_DEFAUT),
  })
  .strict();
export type FluxCleSaisie = z.infer<typeof FluxCleSaisieSchema>;

/** `cle_modifier` : renommer, changer le plafond, ou REMPLACER la valeur. */
export const FluxCleModificationSchema = z
  .object({
    id: z.string().trim().min(1).max(64),
    nom: nomCle.optional(),
    valeur: valeurCle.optional(),
    plafondJourDollars: plafondJour.optional(),
  })
  .strict();
export type FluxCleModification = z.infer<typeof FluxCleModificationSchema>;

/** Ce que l'operation `cles` rend a un editeur de flux (le choix d'une cle dans une etape agent). */
export interface FluxCleChoix {
  id: string;
  nom: string;
  fournisseur: FluxFournisseurCle;
  apercu: string;
}
